/**
 * AssetBrowser — right-side slide-in panel for picking a library asset.
 * Filters by asset type (all/background/foreground), category and a text query
 * (name + tags). Clicking a thumbnail applies it and closes the panel.
 */
import React, { useState, useMemo } from 'react';
import { X, Search, ImageOff } from 'lucide-react';
import { T } from '../../../components/em/tokens';
import { ASSET_CATEGORIES } from '../pgConstants';
import { API_URL } from '../../../api/api';

const TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'background', label: 'Background' },
  { key: 'foreground', label: 'Foreground' },
];

// Resolve a stored asset URL to something the <img> can load.
const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${API_URL || ''}${url}`;
};

const selectStyle = {
  width: '100%', padding: '8px 10px', boxSizing: 'border-box',
  border: `1px solid ${T.border}`, borderRadius: T.rMd,
  background: T.bgCard, color: T.fg1, font: `500 12px/1 ${T.font}`,
  outline: 'none', cursor: 'pointer',
};

export default function AssetBrowser({ open, onClose, assets, loading, onApply }) {
  const [type, setType] = useState('all');
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');

  const list = Array.isArray(assets) ? assets : [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter((asset) => {
      if (type !== 'all' && asset.type !== type) return false;
      if (category !== 'All' && asset.category !== category) return false;
      if (q) {
        const hay = `${asset.name || ''} ${(asset.tags || []).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [list, type, category, query]);

  if (!open) return null;

  const apply = (asset) => { onApply(asset); onClose(); };

  return (
    <div
      className="pg-asset-browser"
      style={{
        position: 'fixed', right: 0, top: 0, bottom: 0, width: 320, zIndex: 1400,
        background: T.bgCard, borderLeft: `1px solid ${T.border}`,
        boxShadow: '-12px 0 40px -16px rgba(0,0,0,0.35)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px', borderBottom: `1px solid ${T.border}`,
      }}>
        <span style={{ font: `700 15px/1.2 ${T.font}`, color: T.fg1 }}>Asset library</span>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          aria-label="Close"
          style={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 0, background: 'transparent', borderRadius: T.rSm, cursor: 'pointer', color: T.fg5 }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Filters */}
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.divider}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'inline-flex', padding: 3, background: T.bgTab, borderRadius: T.rLg, gap: 2 }}>
          {TYPE_FILTERS.map((tf) => {
            const on = type === tf.key;
            return (
              <button
                key={tf.key}
                type="button"
                onClick={() => setType(tf.key)}
                style={{
                  flex: 1, padding: '6px 8px', borderRadius: T.rMd, border: 0, cursor: 'pointer',
                  background: on ? T.bgCard : 'transparent',
                  color: on ? T.orange : T.fg5,
                  font: `${on ? 600 : 500} 12px/1 ${T.font}`,
                  boxShadow: on ? T.shadowSm : 'none',
                }}
              >
                {tf.label}
              </button>
            );
          })}
        </div>

        <select value={category} onChange={(e) => setCategory(e.target.value)} style={selectStyle}>
          <option value="All">All categories</option>
          {ASSET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.fg7 }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search assets…"
            style={{ width: '100%', padding: '8px 10px 8px 32px', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: T.rMd, background: T.bgCard, color: T.fg1, font: `400 12px/1 ${T.font}`, outline: 'none' }}
          />
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: T.fg7, font: `400 13px/1 ${T.font}` }}>
            loading…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '40px 12px', textAlign: 'center' }}>
            <ImageOff size={28} style={{ color: T.fg7, marginBottom: 10 }} />
            <div style={{ color: T.fg5, font: `500 13px/1.4 ${T.font}` }}>No assets found</div>
            <div style={{ color: T.fg7, font: `400 12px/1.4 ${T.font}`, marginTop: 4 }}>
              Try a different filter or search term.
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {filtered.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => apply(asset)}
                title={asset.name}
                className="pg-asset-thumb"
                style={{
                  display: 'flex', flexDirection: 'column', gap: 4, padding: 0,
                  border: `1px solid ${T.border}`, borderRadius: T.rMd, overflow: 'hidden',
                  background: T.bgMuted, cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div style={{ width: '100%', aspectRatio: '1 / 1', overflow: 'hidden', background: T.bgMuted }}>
                  <img
                    src={resolveUrl(asset.url)}
                    alt={asset.name || 'asset'}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </div>
                <span style={{
                  padding: '0 8px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  font: `500 11px/1.3 ${T.font}`, color: T.fg3,
                }}>
                  {asset.name || 'Untitled'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
